import React, { FC, useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';
import { Element, scroller } from 'react-scroll';

import { AnswerTypes } from 'types';

import Header from 'components/Header';
import PlanCard from 'components/PlanCard';
import PaymentModal from 'components/PaymentModal';
import { ReactComponent as EmptyStar } from 'assets/images/empty-star.svg';
import { ReactComponent as Star } from 'assets/images/review-star.svg';

import hexToRGB from 'utils/hexToRgb';
import sendAnalyticsEvent from 'utils/sendAnalyticsEvent';
import { startPolarCheckout } from '../../lib/polar';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Sales: FC<AnswerTypes> = (props) => {
    const { title, text, logoUrl, reviews, logoLink, billingsData, buttonText, discountModalData } = props;
    const [answerItem, setAnswerItems] = useState<Record<string, string>[]>([]);
    const [activePlan, setActivePlan] = useState(billingsData?.activePlan);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDiscount, setIsDiscount] = useState(false);
    const [isSendAnalytic, setIsSendAnalytic] = useState(false);
    const [isLoadingPayment, setIsLoadingPayment] = useState(false);

    const themColor = document.documentElement.style.getPropertyValue('--them-color');
    const maxRating = 5;
    const localQuizId = sessionStorage.getItem('quizId');

    const settings = {
        infinite: true,
        centerMode: true,
        dots: false,
        arrows: false,
        variableWidth: true,
        autoplay: true,
        speed: 2000,
        autoplaySpeed: 2000,
        cssEase: 'linear',
    };

    const plansData = isDiscount ? billingsData?.extraPlans : billingsData?.defaultPlans;

    const onPlanClick = (id: string) => {
        setActivePlan(id);
    };

    const onScroll = () => {
        scroller.scrollTo('PlanSection', {
            duration: 1500,
            smooth: true,
        });
    };

    useEffect(() => {
        if (!isSendAnalytic) {
            sendAnalyticsEvent({
                eventName: 'offer_page_shown',
                data: { screen_name: isDiscount ? 'cancel_offer' : 'offer' },
            });
            setIsSendAnalytic(true);
        }
    }, [isSendAnalytic]);

    useEffect(() => {
        if (isDiscount) {
            setIsSendAnalytic(false);
        }
    }, [isDiscount]);

    useEffect(() => {
        const userAnswer = sessionStorage.getItem('userAnswers');

        if (userAnswer) {
            try {
                const answers: Record<string, any> = JSON.parse(userAnswer);

                const items = Object.values(answers).map((item) => ({
                    question: item.question,
                    answer: item.selectedAnswers.join(', '),
                }));

                setAnswerItems(items);
            } catch {
                // error
            }
        }
    }, []);

    const activePlanData = useMemo(() => plansData?.find((item) => item.id === activePlan), [activePlan]);

    const handleChoosePlan = async () => {
        if (!activePlanData) {
            console.error('No active plan selected');
            return;
        }

        try {
            setIsLoadingPayment(true);

            // Extract numeric price value for analytics
            const priceValue = parseFloat(activePlanData.price?.replace(/[^0-9.]/g, '') || '0');

            // Track payment initiation
            sendAnalyticsEvent({
                eventName: 'polar_checkout_initiated',
                data: {
                    plan_id: activePlanData.id,
                    plan_period: activePlanData.period,
                    price: priceValue,
                    is_discount: isDiscount,
                    tags: activePlanData.tags || [],
                    quiz_id: localQuizId,
                },
            });

            // Get user answers for additional context
            const userAnswersStr = sessionStorage.getItem('userAnswers');
            let userAnswersData = {};
            if (userAnswersStr) {
                try {
                    userAnswersData = JSON.parse(userAnswersStr);
                } catch (e) {
                    console.error('Failed to parse user answers:', e);
                }
            }

            // Use the email collected during the quiz so Polar pre-fills it and
            // the customer email matches the account the magic link logs into.
            const funnelEmail = localStorage.getItem('userEmail') || undefined;

            await startPolarCheckout({
                email: funnelEmail,
                returnPath: `/subscription-success/${localQuizId || 'direct-purchase'}`,
                metadata: {
                    planId: activePlanData.id,
                    planName: `${activePlanData.period} Plan`,
                    quizId: localQuizId || '',
                    source: 'quiz_funnel',
                    tags: JSON.stringify(activePlanData.tags || []),
                    isDiscount,
                    userAnswers: JSON.stringify(userAnswersData),
                    userEmail: funnelEmail || '',
                },
            });
        } catch (error) {
            console.error('❌ Polar checkout error:', error);
            setIsLoadingPayment(false);
            alert('Failed to open payment. Please try again or contact support.');
        }
    };

    return (
        <section className="sales">
            <PaymentModal
                isModalOpen={isModalOpen}
                logoUrl={logoUrl}
                logoLink={logoLink}
                link={activePlanData?.link || ''}
                period={activePlanData?.period || ''}
                price={activePlanData?.price || ''}
                planId={activePlanData?.id || ''}
                discountModalData={discountModalData}
                setIsModalOpen={setIsModalOpen}
                setIsDiscount={setIsDiscount}
                isDiscount={isDiscount}
            />
            <Header logoUrl={logoUrl} isHiddenProgress logoLink={logoLink} />
            <div className="sales__wrapper">
                <h2 dangerouslySetInnerHTML={{ __html: title }} className="sales__title" />
                {text && <p dangerouslySetInnerHTML={{ __html: text }} className="sales__text" />}
                {!!answerItem.length && (
                    <>
                        <p className="sales__answer-title">Your profile summary:</p>
                        <div className="sales__answers-wrap">
                            {answerItem.map(({ question, answer }) => (
                                <div
                                    className="answer_item"
                                    key={question}
                                    style={{ borderColor: `${hexToRGB(themColor, '.7')}` }}
                                >
                                    <p className="answer_item__title" style={{ color: `${hexToRGB(themColor, '.7')}` }}>
                                        {question}
                                    </p>
                                    <p className="answer_item__text">{answer}</p>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <div className="sales__button-wrap">
                    <button className="sales__button" type="button" onClick={onScroll}>
                        {buttonText}
                    </button>
                </div>
            </div>
            <div className="sales__review-wrapper">
                <h3 className="sales__review-title">Reviews</h3>
                <div className="sales__review-wrap">
                    <Slider {...settings}>
                        {!!reviews?.length &&
                            reviews.map(({ avatar, name, rating, text }) => {
                                const activeStar = Array.from({ length: rating }, (v, i) => i + 1);
                                const emptyStar = Array.from({ length: maxRating - rating }, (v, i) => i + 1);

                                return (
                                    <div className="sales__review" key={name} style={{ background: '#fff' }}>
                                        <div className="sales__review-header">
                                            <figure className="sales__review-avatar">
                                                <img src={avatar} alt="" />
                                            </figure>
                                            <div>
                                                <p className="sales__review-name">{name}</p>
                                                <div>
                                                    {activeStar.map((item) => (
                                                        <Star key={item} />
                                                    ))}
                                                    {emptyStar.map((item) => (
                                                        <EmptyStar key={item} />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <p className="sales__review-text">{text}</p>
                                    </div>
                                );
                            })}
                        <div />
                    </Slider>
                </div>
            </div>
            <div className="sales__wrapper">
                {billingsData?.title && (
                    <p dangerouslySetInnerHTML={{ __html: billingsData?.title }} className="sales__plan-title" />
                )}
                {billingsData?.subtitle && (
                    <p dangerouslySetInnerHTML={{ __html: billingsData?.subtitle }} className="sales__plan-subtitle" />
                )}
                {plansData?.map(({ id, period, oldPrice, perDay, link, price, tooltip }) => (
                    <PlanCard
                        key={id}
                        isActive={activePlan === id}
                        onClick={() => {
                            onPlanClick(id);
                        }}
                        id={id}
                        period={period}
                        oldPrice={oldPrice}
                        price={price}
                        perDay={perDay}
                        link={`${link}&intro_offer=${isDiscount}&redirect_uri=${window.location.origin}/thank-page/${localQuizId}`}
                        tooltip={tooltip}
                    />
                ))}
                <Element name="PlanSection">
                    <div className="sales__button-wrapper">
                        <button
                            disabled={!activePlan || isLoadingPayment}
                            className="sales__button"
                            type="button"
                            onClick={handleChoosePlan}
                        >
                            {isLoadingPayment ? 'Opening Payment...' : (billingsData?.buttonText || 'Choose This Plan')}
                        </button>
                    </div>
                </Element>

                <p className="sales__link">Money back guarantee</p>
            </div>
        </section>
    );
};

export default Sales;
